import { useMutation, useQueryClient } from "@tanstack/react-query";

import type {
  ImagingStudyNotification,
  NotificationApiPayload,
} from "@/features/imagings/schemas/imagings";
import { saveTemporaryAccessResult } from "@/features/imagings/services/imagings";
import {
  saveSharedStudyApi,
  sendStudyNotificationAPI,
} from "@/features/imagings/services/imagingService";
import { getUserById } from "@/features/users/services/users";

function toInternationalDigits(phone: string): string {
  return phone.replace(/\D/g, "");
}

/**
 * Hook para compartir estudios de imagen (mismo contrato REST que clinic).
 */
export default function useImagingSharing() {
  const queryClient = useQueryClient();

  const shareImaging = useMutation({
    mutationFn: async (data: ImagingStudyNotification) => {
      const isEmail = data.type === "email";
      const recipientEmail = isEmail ? data.email.toLowerCase() : "";
      const recipientPhone = !isEmail ? toInternationalDigits(data.phone || "") : "";
      let senderEmail = data.institution_email || "";

      if (isEmail) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(recipientEmail)) {
          throw new Error("El formato del correo electrónico es inválido.");
        }
      }

      if (data.institution_id) {
        try {
          const institution = await getUserById(data.institution_id);
          if (institution?.email) {
            senderEmail = institution.email;
          }
        } catch (error) {
          console.error("Error al obtener detalles de la institución:", error);
        }
      }

      const expiration_date = new Date(Date.now() + data.days * 24 * 60 * 60 * 1000);
      const objectSharedId = await saveTemporaryAccessResult({
        created_at: new Date(),
        expiration_date,
        resource_id: data.uid,
        shared_resource: "imaging",
        to: isEmail ? recipientEmail : recipientPhone,
        user_shared: data.institution_id,
      });

      const apiPayload: NotificationApiPayload = {
        userId: data.institution_id,
        objectSharedId,
        imaging: data.uid,
        resource_id: data.uid,
        nameTemplate: "imaging_shared_general",
        typeService: isEmail ? "email" : "whatsapp",
        language: "es",
        recipient: isEmail
          ? { email: recipientEmail, phone: "" }
          : { email: "", phone: recipientPhone },
        trigger: "temporary",
        description: "Estudio",
        manual: true,
      };

      const response = await sendStudyNotificationAPI(apiPayload);

      if (isEmail) {
        await saveSharedStudyApi({
          imaging: data.uid,
          user_receiving: recipientEmail,
          user_sharing: senderEmail,
          phone: data.phone ? [data.phone] : [],
        });
      }

      return {
        token: "",
        shortcutId: objectSharedId,
        trackingId: response?.id || "",
      };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["notifications"] });
      await queryClient.invalidateQueries({ queryKey: ["shortcuts"] });
      await queryClient.invalidateQueries({ queryKey: ["tracking_notifications"] });
    },
  });

  return {
    shareImaging,
  };
}
